<template>
  <div class="progress-page">
    <div class="container">
      <h1 class="page-title">Your Progress</h1>
      <p class="page-subtitle">Track your journey through the 30-Day Challenge</p>
      
      <div class="progress-overview">
        <div class="progress-card">
          <h2>Overall Progress</h2>
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: `${progressPercentage}%` }"></div>
          </div>
          <p class="progress-text">{{ completedDays.length }}/30 days completed</p>
        </div>
        
        <div class="progress-card">
          <h2>Current Streak</h2>
          <div class="streak-count">{{ currentStreak }} days</div>
          <p class="streak-text" v-if="currentStreak > 0">Keep up the great work!</p>
          <p class="streak-text" v-else>Complete today's challenge to start your streak!</p>
        </div>

        <div class="progress-card">
          <h2>Commands Mastered</h2>
          <div class="command-count">{{ totalCommandsIntroduced }}</div>
          <p class="command-text">commands introduced</p>
        </div>
      </div>
      
      <div class="challenge-list">
        <h2>Challenge Progress</h2>
        <div class="challenge-grid">
          <div 
            v-for="day in 30" 
            :key="day"
            class="challenge-card"
            :class="{
              'completed': isCompleted(day),
              'current': currentDay === day,
              'locked': day > currentDay
            }"
          >
            <div class="day-number">Day {{ day }}</div>
            <div class="challenge-status">
              <template v-if="isCompleted(day)">
                <span class="status-icon">✓</span>
                <span class="status-text">Completed</span>
              </template>
              <template v-else-if="currentDay === day">
                <span class="status-icon">▶</span>
                <span class="status-text">Current</span>
              </template>
              <template v-else>
                <span class="status-icon">🔒</span>
                <span class="status-text">Locked</span>
              </template>
            </div>
            <div class="challenge-tasks" v-if="getChallengeForDay(day)">
              <div 
                v-for="task in getChallengeForDay(day).tasks" 
                :key="task.id"
                class="task-item"
                :class="{ 'completed': getTaskStatus(day, task.id) }"
              >
                {{ task.description }}
              </div>
            </div>
            <!-- Add Captain's Log entry if it exists -->
            <div v-if="captainsLog[day]" class="challenge-log">
              <div class="log-title">Captain's Log</div>
              <div class="log-content">{{ captainsLog[day] }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'

export default {
  name: 'ProgressPage',
  computed: {
    ...mapGetters([
      'completedDays',
      'completedCommands',
      'captainsLog',
      'progressPercentage',
      'getTaskStatus'
    ]),
    ...mapState({
      currentDay: state => state.currentDay,
      challenges: state => state.challenges
    }),
    currentStreak() {
      if (!this.completedDays.length) return 0;
      
      const sortedDays = [...this.completedDays].sort((a, b) => a - b);
      let streak = 1;
      
      for (let i = sortedDays.length - 1; i > 0; i--) {
        if (sortedDays[i] - sortedDays[i - 1] === 1) {
          streak++;
        } else {
          break;
        }
      }
      
      return streak;
    },
    totalCommandsIntroduced() {
      // Get all unique commands introduced up to current day
      const uniqueCommands = new Set();
      this.challenges
        .filter(challenge => challenge.day <= this.currentDay)
        .forEach(challenge => {
          challenge.commands.forEach(cmd => {
            uniqueCommands.add(cmd.command);
          });
        });
      return uniqueCommands.size;
    },
    hasNotes() {
      return Object.keys(this.captainsLog).length > 0;
    }
  },
  methods: {
    isCompleted(day) {
      return this.completedDays.includes(day);
    },
    getChallengeForDay(day) {
      return this.challenges.find(challenge => challenge.day === day);
    }
  }
}
</script>

<style scoped>
.progress-page {
  padding: 2rem 0;
}

.page-title {
  text-align: center;
  margin-bottom: 0.5rem;
}

.page-subtitle {
  text-align: center;
  font-size: 1.25rem;
  color: var(--text-light);
  margin-bottom: 2rem;
}

.progress-overview {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 3rem;
}

.progress-card {
  background-color: white;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  text-align: center;
}

.progress-card h2 {
  color: var(--primary);
  margin-bottom: 1rem;
  font-size: 1.25rem;
}

.progress-bar {
  height: 8px;
  background-color: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 1rem;
}

.progress-fill {
  height: 100%;
  background-color: var(--accent);
  transition: width 0.3s ease;
}

.progress-text {
  color: var(--text-light);
  margin: 0;
  font-size: 1.1rem;
}

.streak-count, .command-count {
  font-size: 2.5rem;
  font-weight: 600;
  color: var(--primary);
  margin-bottom: 0.5rem;
}

.streak-text, .command-text {
  color: var(--text-light);
  margin: 0;
}

.challenge-list {
  margin-bottom: 3rem;
}

.challenge-list h2 {
  text-align: center;
  color: var(--primary);
  margin-bottom: 1.5rem;
}

.challenge-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
}

.challenge-card {
  background-color: white;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border: 2px solid transparent;
}

.challenge-card.completed {
  border-color: var(--success);
}

.challenge-card.current {
  border-color: var(--accent);
  transform: translateY(-2px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.challenge-card.locked {
  opacity: 0.7;
}

.day-number {
  font-weight: 600;
  color: var(--primary);
  margin-bottom: 0.5rem;
}

.challenge-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.status-icon {
  font-size: 1.1rem;
}

.status-text {
  font-size: 0.875rem;
  color: var(--text-light);
}

.challenge-tasks {
  font-size: 0.875rem;
}

.task-item {
  padding: 0.25rem 0;
  color: var(--text);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.task-item.completed {
  color: var(--success);
  text-decoration: line-through;
}

.challenge-log {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
}

.log-title {
  font-weight: 600;
  color: var(--primary);
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
}

.log-content {
  font-style: italic;
  color: var(--text);
  font-size: 0.9rem;
  line-height: 1.4;
  background-color: #f8fafc;
  padding: 0.75rem;
  border-radius: 4px;
}

@media (max-width: 768px) {
  .progress-overview {
    grid-template-columns: 1fr;
  }
  
  .challenge-grid {
    grid-template-columns: 1fr;
  }
  
  .challenge-card {
    margin: 0 1rem;
  }
}
</style>